// Market cart — the buyer's lines before checkout.
//
// Storage: this browser's localStorage, deliberately: a cart holds no money and
// commits to nothing. Prices are locked only at checkout, by the server's quote
// (market-checkout; docs/HIOBUY_CHECKOUT_DESIGN.md).
//
// A line stores only WHAT was chosen: product, variant key, quantity, and the
// unit price the trader saw when adding it. Every price shown in the cart is
// recomputed from fresh data through marketPricing.quote — the stored price is
// used for one thing only: telling the trader it changed.
//
// Ordering rules, as HioBuy's order preview enforced them (2026-10-19, see
// marketPricing.quote for the exact cases):
//   • the wholesale TIER is chosen by the product's total across its variants;
//   • the MINIMUM is met by the product's total across its variants;
//   • the ordering STEP applies to each variant on its own.
// Stock is per variant.

import { quote, toSar } from './marketPricing';

const KEY = 'maabar_market_cart_v1';
const EVENT = 'maabar-market-cart';
const MAX_LINES = 100;

const read = () => {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY) || '[]');
    return Array.isArray(v) ? v.filter((l) => l && typeof l.product_id === 'string' && Number(l.qty) > 0) : [];
  } catch { return []; }
};
const write = (lines) => {
  try { window.localStorage.setItem(KEY, JSON.stringify(lines.slice(0, MAX_LINES))); } catch { /* private mode: cart lives for the page only */ }
  try { window.dispatchEvent(new Event(EVENT)); } catch { /* non-browser */ }
};

export const lineId = (l) => `${l.product_id}|${l.sku_key ?? ''}`;
export function readCart() { return read(); }

// The cart's one shipping method in the delivered-price model (owner,
// 2026-10-29): 'air' | 'sea' | 'fast'. The buyer's own pick in the cart or at
// checkout — kept only for the cart it was made on: once the cart changes it
// is forgotten and the cheaper method is shown again. A preference only —
// checkout re-prices it.
const METHOD_KEY = 'maabar_market_cart_method_v2';
const cartSig = () => read().map((l) => `${lineId(l)}x${l.qty}`).sort().join(',');
export function getCartMethod() {
  try {
    const v = JSON.parse(window.localStorage.getItem(METHOD_KEY) || 'null');
    return v && v.sig === cartSig() ? v.m : null;
  } catch { return null; }
}
export function setCartMethod(m) {
  try { window.localStorage.setItem(METHOD_KEY, JSON.stringify({ m, sig: cartSig() })); } catch { /* private mode */ }
  window.dispatchEvent(new Event(EVENT));
}
export function cartCount() { return read().length; }

// What the cart sends to checkout (point 2, owner 2026-11-02): ONE option and only the lines it
// carries — or «اطلب سعر الشحن» for lines no rate covers (ask). The other lines stay in the cart.
// Kept for the cart it was made on: if a chosen line is gone or its quantity changed, it is void.
const SEL_KEY = 'maabar_market_checkout_sel_v1';
export function setCheckoutSelection(sel) {
  try { window.localStorage.setItem(SEL_KEY, JSON.stringify(sel)); } catch { /* private mode: checkout takes the whole cart */ }
}
/** { method: 'sea'|'air'|'fast', keys: ['pid|sku x qty'…], ask: boolean } — or null (the whole cart). */
export function getCheckoutSelection() {
  try {
    const v = JSON.parse(window.localStorage.getItem(SEL_KEY) || 'null');
    if (!v || !Array.isArray(v.keys) || !v.keys.length) return null;
    const have = new Set(read().map((l) => `${lineId(l)}x${l.qty}`));
    return v.keys.every((k) => have.has(k)) ? v : null;
  } catch { return null; }
}
export const selectionKey = (l) => `${lineId(l)}x${l.qty}`;
/** After an order: the lines it took leave the cart; the rest (asked for later, other options) stay. */
export function removeLines(keys) {
  const k = new Set(keys);
  write(read().filter((l) => !k.has(selectionKey(l))));
  try { window.localStorage.removeItem(SEL_KEY); } catch { /* fine */ }
}

/** Add a variant; the same variant again adds to its quantity. */
// What the buyer saw: the FACTORY unit price in CNY at the quantity it was
// shown for (owner, 2026-10-29: «Only show this notice when the factory price
// itself changed … Never because of the model switch»). CNY, so an exchange
// rate move is not a "change"; the quantity, so a tier the buyer moved into by
// changing the quantity is not one either. unit_sar_seen is kept for old lines.
export function addToCart({ product_id, sku_key, qty, unit_sar, unit_cny }) {
  const lines = read();
  const id = lineId({ product_id, sku_key });
  const hit = lines.find((l) => lineId(l) === id);
  const seen = { unit_sar_seen: unit_sar ?? null, unit_cny_seen: unit_cny ?? null, qty_seen: qty };
  if (hit) { hit.qty += qty; if (unit_cny != null) Object.assign(hit, seen); }
  else lines.push({ product_id, sku_key: sku_key ?? null, qty, ...seen, added_at: new Date().toISOString() });
  write(lines);
  return lines;
}
export function setLineQty(id, qty) {
  write(read().map((l) => (lineId(l) === id ? { ...l, qty: Math.max(1, Math.floor(Number(qty) || 1)) } : l)));
}
export function removeLine(id) { write(read().filter((l) => lineId(l) !== id)); }
export function clearCart() { write([]); }
export function onCartChange(fn) {
  const h = () => fn(read());
  window.addEventListener(EVENT, h);
  window.addEventListener('storage', h);
  return () => { window.removeEventListener(EVENT, h); window.removeEventListener('storage', h); };
}

/**
 * Price and validate a cart against fresh data.
 *   products  { [id]: product row (any status) }   skus  { [id]: sku rows }
 * Returns groups per product, each line with its quote and issues, and the
 * goods subtotal over VALID lines only. Issue codes:
 *   gone          product no longer in the catalog
 *   unavailable   product not active, or the variant out of stock / withdrawn
 *   sku_gone      the variant no longer exists
 *   below_moq     the PRODUCT's total is under its minimum (flagged on each of its lines)
 *   step          not a multiple of the ordering step
 *   over_stock    more than the variant's stock
 *   price_changed the supplier's price moved since adding (info, not blocking) — judged
 *                 at the line's own quantity, so the product-total tier never trips it
 */
/** How much is missing to reach the minimum cart value (0 when there is none or it is reached), rounded up to the halala. */
export function cartShortfall(subtotalSar, minSar) {
  const min = Number(minSar) || 0, sub = Number(subtotalSar) || 0;
  return min > 0 && sub < min ? Math.ceil((min - sub) * 100 - 1e-9) / 100 : 0;
}

/** Issues that make a line unsellable as it is — it is left out of the order, never blocking the rest. */
export const DROPPED = ['gone', 'unavailable', 'sku_gone'];

export function priceCart(lines, products, skus, rates) {
  const groups = new Map();
  for (const l of lines) {
    if (!groups.has(l.product_id)) groups.set(l.product_id, []);
    groups.get(l.product_id).push(l);
  }
  const out = [];
  let subtotal = 0;
  let blocking = 0, dropped = 0, fixable = 0, sellableCount = 0;
  for (const [pid, ls] of groups) {
    const product = products[pid] || null;
    const list = skus[pid] || [];
    const productQty = ls.reduce((n, l) => n + l.qty, 0);
    const rows = ls.map((l) => {
      const issues = [];
      const sku = l.sku_key ? list.find((s) => s.key === l.sku_key) || null : list[0] || null;
      if (!product) issues.push('gone');
      else if (product.status !== 'active') issues.push('unavailable');
      if (product && l.sku_key && !sku) issues.push('sku_gone');
      if (sku && (!sku.available || !(Number(sku.stock) > 0))) issues.push('unavailable');
      const q = product ? quote(product, list, sku, l.qty, rates, productQty) : null;
      if (q && sku && !issues.length) {
        if (!q.stepOk) issues.push('step');
        if (sku.stock != null && l.qty > Number(sku.stock)) issues.push('over_stock');
      }
      // The factory price itself, in CNY, at the quantity the buyer saw it for
      // (never the delivered price, a rate move or a new tier from a new
      // quantity). A line saved before this was recorded shows no notice.
      const seenQty = Number(l.qty_seen) > 0 ? Number(l.qty_seen) : null;
      const alone = product && seenQty && l.unit_cny_seen != null ? quote(product, list, sku, seenQty, rates) : null;
      const priceChanged = alone?.unitCny != null && Math.abs(Number(alone.unitCny) - Number(l.unit_cny_seen)) >= 0.005;
      const oldUnitSar = priceChanged ? toSar(Number(l.unit_cny_seen), rates) : null;
      return { id: lineId(l), line: l, sku, quote: q, issues, priceChanged, oldUnitSar, valid: !issues.length && q?.totalSar != null };
    });
    // The minimum is the product's, met by its total: the largest minimum any
    // of its lines states (product MOQ, or a SKU's, rounded up to the step).
    const productMin = Math.max(0, ...rows.map((r) => r.quote?.moq || 0));
    if (product && productQty < productMin) {
      for (const r of rows) if (r.quote && r.sku && !r.issues.some((x) => ['gone', 'unavailable', 'sku_gone'].includes(x))) { r.issues.push('below_moq'); r.valid = false; }
    }
    for (const r of rows) {
      if (r.valid) { subtotal += r.quote.totalSar; sellableCount++; }
      else { blocking++; if (r.issues.some((x) => DROPPED.includes(x))) dropped++; else fixable++; }
    }
    out.push({ product_id: pid, product, productQty, productMin, lines: rows });
  }
  // A line that cannot be sold (gone / unavailable / variant gone) never blocks the rest (store test,
  // 2026-11-03): it is left out of the order and says so; only a line the buyer can FIX (quantity,
  // step, stock) holds the cart. `valid` keeps its old meaning (every line sellable as it stands).
  return { groups: out, subtotalSar: Math.round(subtotal * 100) / 100, blocking, dropped, fixable,
    sellable: sellableCount > 0 && fixable === 0, valid: lines.length > 0 && blocking === 0 };
}
